import { Type, type Static } from "@sinclair/typebox";
import { Cents, LatLng, Place, PlaceInput } from "./domain.ts";
import { PixPayment } from "./pix.ts";

/**
 * Next features (docs/specs/next-features.md): paradas, gorjetas, cupons, Carteira (card after the
 * trip), recibos, segurança (SOS, incidents, audio), account and privacy. Every rule's numbers are
 * remote config (`packages/db/src/config-defaults.ts`).
 */

// ── Paradas ──────────────────────────────────────────────────────────────────────────────
/** Up to `trip.max_stops` places between pickup and dropoff, in order. */
export const TripStopsInput = Type.Array(PlaceInput, { maxItems: 2, $id: "TripStopsInput" });

export const TripStopView = Type.Object(
  {
    /** 1-based, in route order. */
    seq: Type.Integer({ minimum: 1 }),
    place: Place,
    arrivedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    departedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "TripStopView" },
);
export type TripStopView = Static<typeof TripStopView>;

// ── Gorjeta ──────────────────────────────────────────────────────────────────────────────
/** `POST /trips/{id}/tip`: within `tip.window_days` of completion, at most `tip.max_cents`. */
export const TipInput = Type.Object({ amountCents: Type.Integer({ minimum: 100 }) }, { $id: "TipInput" });
export const TipStatus = Type.Union([Type.Literal("pending"), Type.Literal("paid"), Type.Literal("failed"), Type.Literal("cancelled")], { $id: "TipStatus" });
export const TipView = Type.Object(
  { amountCents: Cents, status: TipStatus, paidAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]) },
  { $id: "TipView" },
);
export type TipView = Static<typeof TipView>;
/** Pix trips get a Pix to pay; card trips charge the trip's card (pix null). */
export const TipCreated = Type.Object({ tip: TipView, pix: Type.Union([PixPayment, Type.Null()]) }, { $id: "TipCreated" });

// ── Cupom ────────────────────────────────────────────────────────────────────────────────
/** A code checked on a quote: `discountCents` per option is in QuoteResponse. */
export const PromoQuote = Type.Object(
  {
    code: Type.String(),
    valid: Type.Boolean(),
    /** pt-BR: "10% de desconto, até R$ 8,00" or why it can't be used ("Cupom expirado"). */
    message: Type.String(),
  },
  { $id: "PromoQuote" },
);
export type PromoQuote = Static<typeof PromoQuote>;
/** Why a code was refused (`error` of a 409/422 on quote or request). */
export const PROMO_ERRORS = ["promo_not_found", "promo_expired", "promo_exhausted", "promo_already_used", "promo_first_ride_only", "promo_not_eligible", "promo_min_fare"] as const;

// ── Carteira (card after the trip) ───────────────────────────────────────────────────────
export const SavedCardView = Type.Object(
  {
    id: Type.String(),
    /** Mercado Pago payment_method_id: "visa", "master", "elo", "amex"… */
    brand: Type.String(),
    last4: Type.String(),
    expMonth: Type.Integer(),
    expYear: Type.Integer(),
    isDefault: Type.Boolean(),
    status: Type.Union([Type.Literal("active"), Type.Literal("disabled")]),
    /** pt-BR, when disabled: "Pagamento recusado em 12/10". */
    disabledReason: Type.Union([Type.String(), Type.Null()]),
  },
  { $id: "SavedCardView" },
);
export type SavedCardView = Static<typeof SavedCardView>;

export const RiderDebtView = Type.Object(
  { id: Type.String(), tripId: Type.String(), tripCode: Type.String(), amountCents: Cents, createdAt: Type.String({ format: "date-time" }) },
  { $id: "RiderDebtView" },
);
export type RiderDebtView = Static<typeof RiderDebtView>;

/** `GET /me/wallet`. */
export const WalletView = Type.Object(
  {
    card: Type.Object({
      /** ≥ `payment.card_after_trip_min_trips` completed trips and no payment issues. */
      eligible: Type.Boolean(),
      /** Why not, when not eligible. */
      reason: Type.Union([Type.Literal("min_trips"), Type.Literal("payment_issue"), Type.Literal("open_debt"), Type.Null()]),
      minTrips: Type.Integer(),
      completedTrips: Type.Integer(),
      /** Mercado Pago public key for tokenizing in the app (empty with the fake provider). */
      publicKey: Type.String(),
    }),
    cards: Type.Array(SavedCardView),
    /** Failed card charges to settle by Pix before the next request. */
    debts: Type.Array(RiderDebtView),
  },
  { $id: "WalletView" },
);
export type WalletView = Static<typeof WalletView>;

/** `POST /me/cards`: a card token created in the app with the provider's public key (never the number). */
export const AddCardInput = Type.Object(
  {
    token: Type.String({ minLength: 4, maxLength: 200 }),
    /** Mercado Pago payment_method_id, as the app's BIN lookup returned it. */
    brand: Type.String({ maxLength: 40 }),
    isDefault: Type.Optional(Type.Boolean()),
  },
  { $id: "AddCardInput" },
);
/** `POST /me/debts/{id}/pay`. */
export const DebtPayment = Type.Object({ debt: RiderDebtView, pix: PixPayment }, { $id: "DebtPayment" });

// ── Recibo ───────────────────────────────────────────────────────────────────────────────
/** `GET /trips/{id}/receipt`: a signed link to the HTML receipt (open in a browser sheet, share). */
export const ReceiptLink = Type.Object({ url: Type.String(), expiresAt: Type.String({ format: "date-time" }) }, { $id: "ReceiptLink" });
/** `PATCH /me/preferences` (only the fields set change); `GET` and `PATCH` return `RiderPreferencesView`. */
export const RiderPreferencesInput = Type.Object(
  { receiptEmails: Type.Optional(Type.Boolean()), recordAudio: Type.Optional(Type.Boolean()) },
  { $id: "RiderPreferencesInput" },
);
/** `PATCH /me/preferences` and `GET /me/preferences`. */
export const RiderPreferencesView = Type.Object({ receiptEmails: Type.Boolean(), recordAudio: Type.Boolean() }, { $id: "RiderPreferencesView" });

// ── Segurança: SOS, incidents, audio ─────────────────────────────────────────────────────
export const SAFETY_INCIDENT_CATEGORIES = ["assedio", "direcao_perigosa", "agressao", "roubo", "acidente", "veiculo_diferente", "outro"] as const;
// Spelled out: a `.map()` over the list types as `never` in TypeBox's Static.
export const SafetyIncidentCategory = Type.Union(
  [
    Type.Literal("assedio"), Type.Literal("direcao_perigosa"), Type.Literal("agressao"), Type.Literal("roubo"), Type.Literal("acidente"),
    Type.Literal("veiculo_diferente"), Type.Literal("outro"),
  ],
  { $id: "SafetyIncidentCategory" },
);
export type SafetyIncidentCategory = Static<typeof SafetyIncidentCategory>;
export const SafetyIncidentKind = Type.Union([Type.Literal("sos"), Type.Literal("report")], { $id: "SafetyIncidentKind" });
export const SafetyIncidentStatus = Type.Union([Type.Literal("open"), Type.Literal("acknowledged"), Type.Literal("resolved")], { $id: "SafetyIncidentStatus" });

/** `POST /trips/{id}/sos` ("Alertar a central"): also texts trusted contacts the share link. */
export const SosInput = Type.Object({ location: Type.Optional(LatLng) }, { $id: "SosInput" });
/** `POST /me/incidents` ("Relatar um incidente"). */
export const IncidentReportInput = Type.Object(
  {
    tripId: Type.Optional(Type.String()),
    category: SafetyIncidentCategory,
    description: Type.String({ minLength: 1, maxLength: 2000 }),
    /** Recordings uploaded with `POST /trips/{id}/audio` to attach. */
    recordingIds: Type.Optional(Type.Array(Type.String(), { maxItems: 5 })),
    /** The app reporting, when there is no trip (a person can be rider and driver). */
    app: Type.Optional(Type.Union([Type.Literal("rider"), Type.Literal("driver")])),
  },
  { $id: "IncidentReportInput" },
);
export const SafetyIncidentView = Type.Object(
  {
    id: Type.String(),
    kind: SafetyIncidentKind,
    status: SafetyIncidentStatus,
    tripId: Type.Union([Type.String(), Type.Null()]),
    category: Type.Union([SafetyIncidentCategory, Type.Null()]),
    description: Type.Union([Type.String(), Type.Null()]),
    recordings: Type.Integer({ description: "Audio files attached" }),
    contactsNotified: Type.Integer(),
    createdAt: Type.String({ format: "date-time" }),
    acknowledgedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    resolvedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "SafetyIncidentView" },
);
export type SafetyIncidentView = Static<typeof SafetyIncidentView>;

/** `GET /safety/audio-key`: the server's RSA public key that wraps each recording's AES key. */
export const AudioKeyView = Type.Object(
  {
    keyId: Type.String(),
    /** SPKI PEM. */
    publicKeyPem: Type.String(),
    /**
     * Key wrap RSA-OAEP with SHA-256 and MGF1-SHA-256 (Android: pass an explicit
     * `OAEPParameterSpec` with `MGF1ParameterSpec.SHA256`); content AES-256-GCM, 12-byte nonce,
     * body = ciphertext ‖ 16-byte tag.
     */
    algorithm: Type.Literal("RSA-OAEP-256+A256GCM"),
  },
  { $id: "AudioKeyView" },
);
/** The metadata form fields of `POST /trips/{id}/audio` (multipart, next to `file`). */
export const AudioUploadMeta = Type.Object(
  {
    wrappedKey: Type.String({ description: "Base64 RSA-OAEP (SHA-256, MGF1-SHA-256) of the 32-byte AES key" }),
    iv: Type.String({ description: "Base64 12-byte AES-GCM nonce" }),
    keyId: Type.String(),
    mimeType: Type.String({ description: "Of the plaintext: audio/mp4 (AAC)" }),
    durationSeconds: Type.Integer({ minimum: 1 }),
    startedAt: Type.String({ format: "date-time" }),
    endedAt: Type.String({ format: "date-time" }),
    incidentId: Type.Optional(Type.String()),
  },
  { $id: "AudioUploadMeta" },
);
export const AudioUploaded = Type.Object({ id: Type.String(), expiresAt: Type.String({ format: "date-time" }) }, { $id: "AudioUploaded" });

// ── Conta e privacidade ──────────────────────────────────────────────────────────────────
/** `POST /me/email` sends a 6-digit code to the new address; `POST /me/email/verify` confirms it. */
export const EmailChangeInput = Type.Object({ email: Type.String({ format: "email", maxLength: 200 }) }, { $id: "EmailChangeInput" });
export const EmailVerifyInput = Type.Object({ email: Type.String({ format: "email", maxLength: 200 }), code: Type.String({ pattern: "^[0-9]{6}$" }) }, { $id: "EmailVerifyInput" });
export const SignInMethod = Type.Union([Type.Literal("phone"), Type.Literal("apple"), Type.Literal("google"), Type.Literal("email")], { $id: "SignInMethod" });

/** The deletion request as the apps show it (Privacidade → Excluir conta). `DELETE /me/deletion-request` cancels while pending. */
export const DeletionStatusView = Type.Object(
  {
    status: Type.Union([Type.Literal("pending"), Type.Literal("blocked")]),
    requestedAt: Type.String({ format: "date-time" }),
    /** When the account is anonymized, if nothing blocks it. */
    scheduledFor: Type.String({ format: "date-time" }),
    /** pt-BR, when blocked: "Você tem uma corrida em andamento" / "Há um saldo a receber". */
    blockedReason: Type.Union([Type.String(), Type.Null()]),
  },
  { $id: "DeletionStatusView" },
);
